"use client";

import { Fragment, type CSSProperties, type ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import { useListMembers } from "@/lib/api/generated";

const MENTION_TOKEN_RE = /<@([0-9a-f-]{36})>/gi;

const MENTION_BADGE_STYLE: CSSProperties = {
  ["--gradient-color" as string]: "#2C7EFF",
};

type Segment =
  | { type: "text"; value: string }
  | { type: "mention"; id: string };

function tokenize(content: string): Segment[] {
  const segments: Segment[] = [];
  let last = 0;
  let match: RegExpExecArray | null;

  MENTION_TOKEN_RE.lastIndex = 0;
  while ((match = MENTION_TOKEN_RE.exec(content))) {
    if (match.index > last) {
      segments.push({ type: "text", value: content.slice(last, match.index) });
    }
    segments.push({ type: "mention", id: match[1] });
    last = match.index + match[0].length;
  }

  if (last < content.length) {
    segments.push({ type: "text", value: content.slice(last) });
  }

  return segments;
}

function collectMentionIds(content: string): string[] {
  const ids = new Set<string>();
  let match: RegExpExecArray | null;

  MENTION_TOKEN_RE.lastIndex = 0;
  while ((match = MENTION_TOKEN_RE.exec(content))) {
    ids.add(match[1].toLowerCase());
  }

  return Array.from(ids).sort();
}

function MentionBadge({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <Badge
      variant="linear"
      size="xs"
      className="text-[#83b9ff] align-baseline"
      style={MENTION_BADGE_STYLE}
    >
      {children}
    </Badge>
  );
}

export function NotificationMentionPreview({ content }: { content: string }) {
  const ids = collectMentionIds(content);
  const { data, isFetching } = useListMembers(
    {
      ids: ids.length > 0 ? ids : undefined,
      per_page: 100,
      include_suspended: true,
    },
    {
      query: {
        enabled: ids.length > 0,
        staleTime: 60_000,
      },
    },
  );
  const members = new Map(
    (data?.members ?? []).map((member) => [member.id.toLowerCase(), member]),
  );

  return (
    <>
      {tokenize(content).map((segment, index) => {
        if (segment.type === "text") {
          const lines = segment.value.split("\n");
          return (
            <Fragment key={index}>
              {lines.map((line, lineIndex) => (
                <Fragment key={lineIndex}>
                  {line}
                  {lineIndex < lines.length - 1 && <br />}
                </Fragment>
              ))}
            </Fragment>
          );
        }

        const member = members.get(segment.id.toLowerCase());
        const name = member
          ? `${member.first_name ?? ""} ${member.last_name ?? ""}`.trim() ||
            member.email
          : null;

        if (!name && isFetching) {
          return (
            <MentionBadge key={index}>
              <span className="inline-block h-3 w-16 animate-pulse rounded-sm bg-[#83b9ff]/20" />
            </MentionBadge>
          );
        }

        return <MentionBadge key={index}>@{name ?? segment.id}</MentionBadge>;
      })}
    </>
  );
}
